<template>
  <div>
    <FilledBanner title="获取最新行业资讯" subtitle="法务团队为您提供全球最新的法律政策和合规建议"
      bg-image="https://video.reta-inc.com/image/default/FC1EA403B458425D98DD326C415DC1DC-6-2.jpeg" />
    <div class="main">
      <InfoHeader @search="onSearch" />
      <div class="articles">
        <div class="articles__main">
          <div class="articles__empty" v-if="!data?.list || !data?.list.length">
            <div class="articles__empty-img"></div>
            <div class="articles__empty-title">搜索失败</div>
            <div class="articles__empty-subtitle">没有找到内容，<span @click="onClearClick">清除条件</span></div>
          </div>
          <div class="articles__list" v-else>
            <nuxt-link v-for="(item, index) in data?.list" :key="index" :to="item.url" class="articles__item">
              <div class="articles__cover">
                <img :src="item?.attributes?.cover?.data?.attributes?.url || defaultCoverUrl"
                  :onerror="`this.src=${defaultCoverUrl}`" alt="" />
              </div>
              <div class="articles__content">
                <div>{{ item.attributes.title }}</div>
                <span>{{ formatDate(item.attributes.displayPublishedAt || item.attributes.publishedAt) }}</span>
              </div>
            </nuxt-link>
          </div>
          <a-pagination :total="data.total" :current="pageNo" :page-size="9" @change="onPageChange"
            v-if="data?.list && data?.list.length" />
        </div>
        <div class="articles__recommend">
          <ArticleRecommend :exclude-ids="data?.list?.map(item => item.id)" :category="category" />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { IList } from '@/@types/cms';
import { IArticle } from '@/@types/entities';
import qs from 'qs';
import { useDateFormat } from '@vueuse/core';

const route = useRoute();
const pageNo = ref(1);
const { page = 1 } = route.query;
if (page) {
  pageNo.value = Number(page);
}
const category = route.params.category as string;
const defaultCoverUrl = ref('');

useSeoHead();

if (category === 'policy') {
  defaultCoverUrl.value = 'https://video.reta-inc.com/image/default/D9F51EB0251A4DA6B3DC4CFE204E2049-6-2.png';
} else if (category === 'news') {
  defaultCoverUrl.value = 'https://video.reta-inc.com/image/default/67F2F183613049698F18F30A240AB3E8-6-2.png';
} else if (category === 'guide') {
  defaultCoverUrl.value = 'https://video.reta-inc.com/image/default/D7CBCCFEA6C8415D84FBF446A143CFCF-6-2.png';
}

const { data, refresh } = await useAsyncData(async () => {
  const query: {
    page?: number;
    keyword?: string;
    category: string;
  } = {
    category: route.params.category as string,
  };
  const { page = 1, keyword = '' } = route.query;
  if (Number(page) > 1) {
    query.page = Number(page);
  }
  if (keyword) {
    query.keyword = keyword as string;
  }
  const queryStr = qs.stringify(query);
  const { data } = await useFetch<IList<IArticle>>(`/api/article/list?${queryStr}`);
  const list = data.value?.data.map(item => ({
    ...item,
    url: `/info/${item.attributes.category.data.attributes.name}/${item.id}`,
    // url: `/info/detail/${item.attributes.title}`,
  }));
  return {
    list,
    total: data.value?.meta.pagination.total || 0,
  };
});

function formatDate(dateStr: string) {
  if (!dateStr) {
    return '';
  }
  return useDateFormat(new Date(dateStr), 'YYYY-MM-DD').value;
}

const globalLoading = inject('global-loading', ref(false));

async function onClearClick() {
  await navigateTo({
    path: route.path,
  });
  globalLoading.value = true;
  await refresh();
  globalLoading.value = false;
}

async function onSearch(keyword: string) {
  await navigateTo({
    path: '/help-center/search',
    query: { keyword },
  });
}

async function onPageChange(page: number) {
  pageNo.value = page;
  await navigateTo({
    path: route.path,
    query: {
      ...route.query,
      page,
    },
  });
  globalLoading.value = true;
  await refresh();
  globalLoading.value = false;
}
</script>
<style lang="less" scoped>
@media screen and (min-width: 800px) {
  .main {
    width: 1280px;
    margin: 40px auto;
    padding: 0 32px;
  }

  .articles {
    margin: 60px 0 40px;
    display: flex;

    &__main {
      line-height: normal;
    }

    &__empty {
      width: 832px;
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 60px;
      margin-bottom: 214px;
    }

    &__empty-img {
      width: 180px;
      height: 180px;
      background-image: url('@/assets/images/article-empty.svg');
      background-repeat: no-repeat;
      background-size: contain;
    }

    &__empty-title {
      font-size: 18px;
      line-height: 25px;
      color: #1d2129;
      font-weight: 600;
    }

    &__empty-subtitle {
      font-size: 16px;
      line-height: 22px;
      color: #86909c;
      margin-top: 8px;

      span {
        color: #165dff;
        font-weight: 500;
        cursor: pointer;
      }
    }

    &__list {
      width: 832px;
      height: 860px;
      display: grid;
      grid-template-columns: 264px 264px 264px;
      grid-template-rows: 260px 260px 260px;
      grid-row-gap: 40px;
      grid-column-gap: 20px;
    }

    .arco-pagination {
      margin-top: 40px;
      display: flex;
      justify-content: center;
    }

    &__item {
      width: 264px;
      height: 260px;
      overflow: hidden;
      display: block;
      cursor: pointer;
      text-decoration: none;

      &:hover {
        box-shadow: 0px 4px 16px rgba(135, 172, 255, 0.2);

        .articles__content > div {
          color: #165dff;
        }
      }
    }

    &__cover {
      width: 264px;
      height: 140px;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      overflow: hidden;
      background: #e5e7eb;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__content {
      border: 1px solid #e5e7eb;
      border-top: 0;
      height: 120px;
      box-sizing: border-box;
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
      position: relative;

      & > div {
        font-size: 16px;
        line-height: 24px;
        color: #1d2129;
        max-height: 84px;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        padding: 12px 16px 0;
        box-sizing: border-box;
        word-break: break-all;
        display: -webkit-box;
        text-decoration: none;
      }

      & > span {
        font-size: 12px;
        line-height: 17px;
        letter-spacing: 0.02em;
        color: #86909c;
        position: absolute;
        right: 16px;
        bottom: 12px;
      }
    }

    &__recommend {
      width: 100%;
      margin-left: 70px;
    }
  }
}
</style>
<style lang="less" scoped>
@media screen and (max-width: 800px) {
  .main {
    max-width: 100%;
    margin: 16px auto;
    padding: 0 16px;
  }

  .articles {
    margin: 16px 0 40px;
    display: flex;
    flex-direction: column;

    &__main {
      line-height: normal;
    }

    &__empty {
      width: 832px;
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 60px;
      margin-bottom: 214px;
    }

    &__empty-img {
      width: 180px;
      height: 180px;
      background-image: url('@/assets/images/article-empty.svg');
      background-repeat: no-repeat;
      background-size: contain;
    }

    &__empty-title {
      font-size: 18px;
      line-height: 25px;
      color: #1d2129;
      font-weight: 600;
    }

    &__empty-subtitle {
      font-size: 16px;
      line-height: 22px;
      color: #86909c;
      margin-top: 8px;

      span {
        color: #165dff;
        font-weight: 500;
        cursor: pointer;
      }
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .arco-pagination {
      margin-top: 40px;
      display: flex;
      justify-content: center;
    }

    &__item {
      overflow: hidden;
      display: flex;
      justify-content: space-between;
      cursor: pointer;
      text-decoration: none;

      // &:hover {
      //   box-shadow: 0px 4px 16px rgba(135, 172, 255, 0.2);
      //   .articles__content > a {
      //     color: #165dff;
      //   }
      // }
      .articles__cover {
        img {
          width: 140px;
          height: 76px;
          object-fit: cover;
          border-radius: 4px;
        }
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      flex: 1;
      padding-left: 16px;

      & > div {
        font-weight: 400;
        font-size: 14px;
        line-height: 24px;
        letter-spacing: 0.01em;
        color: #1d2129;
        text-decoration: none;
      }

      & > span {
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        letter-spacing: 0.02em;
        color: #86909c;
      }
    }

    &__recommend {
      // margin-left: 70px;
      margin-top: 45px;
    }
  }
}
</style>
